import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, Button, Badge } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';

const ADMIN_ROLES = new Set(['admin', 'super_admin']);

function TransferStatusBadge({ status }) {
  const variant =
    status === 'approved' ? 'success' : status === 'denied' ? 'danger' : 'warning';
  const label =
    status === 'pending' ? 'Pending' : status === 'approved' ? 'Approved' : 'Denied';
  return <Badge variant={variant}>{label}</Badge>;
}

export default function TransfersTab({ onPendingCountChange }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const agencyId = user?.agency_id;
  const isAdmin = ADMIN_ROLES.has(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [denyLoadingId, setDenyLoadingId] = useState(null);

  const loadData = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const [allRows, count] = await Promise.all([
        api.ownershipTransfers.getForAgency(agencyId),
        api.ownershipTransfers.getPendingActionCount(agencyId),
      ]);
      setRows(allRows || []);
      onPendingCountChange?.(count ?? 0);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load transfer requests', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [agencyId, addToast, onPendingCountChange]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const resolved = useMemo(
    () => rows.filter((r) => r.status === 'approved' || r.status === 'denied'),
    [rows],
  );

  const isManagerOriginated = (row) =>
    row.requester_role === 'manager'
    || (typeof row.notes === 'string' && row.notes.startsWith('Proposed new owner:'));

  const canResolve = (row) => {
    if (isAdmin) return true;
    if (row.current_owner_id !== user?.id) return false;
    // Manager-originated requests: owner cannot resolve (Admin/Super Admin only).
    if (isManagerOriginated(row)) return false;
    return true;
  };

  const ownerAwaitingAdmin = (row) =>
    !isAdmin
    && row.current_owner_id === user?.id
    && isManagerOriginated(row);

  const handleResolve = async (requestId, approved) => {
    if (!user?.id) return;
    if (approved) setApproveLoadingId(requestId);
    else setDenyLoadingId(requestId);
    try {
      await api.ownershipTransfers.resolve(requestId, {
        approved,
        resolvedBy: user.id,
      });
      addToast({
        type: 'success',
        title: approved ? 'Transfer approved' : 'Transfer denied',
      });
      window.dispatchEvent(new CustomEvent('transfer-pending-change'));
      await loadData();
    } catch (err) {
      addToast({
        type: 'error',
        title: approved ? 'Approve failed' : 'Deny failed',
        message: err.message,
      });
    } finally {
      setApproveLoadingId(null);
      setDenyLoadingId(null);
    }
  };

  const renderTable = (items, { grayed = false, showActions = false } = {}) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 font-medium text-gray-500">Lead</th>
            <th className="px-4 py-3 font-medium text-gray-500">Requested By</th>
            <th className="px-4 py-3 font-medium text-gray-500">Current Owner</th>
            <th className="px-4 py-3 font-medium text-gray-500">Requested At</th>
            <th className="px-4 py-3 font-medium text-gray-500">Notes</th>
            <th className="px-4 py-3 font-medium text-gray-500">Status</th>
            {showActions && (
              <th className="px-4 py-3 font-medium text-gray-500 text-right">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className={grayed ? 'text-gray-400' : undefined}>
          {items.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 align-top">
              <td className={`px-4 py-3 font-medium ${grayed ? '' : 'text-gray-900'}`}>
                {row.lead_name}
              </td>
              <td className="px-4 py-3">{row.requester_name}</td>
              <td className="px-4 py-3">{row.current_owner_name}</td>
              <td className="px-4 py-3 whitespace-nowrap">
                {row.created_at
                  ? format(parseISO(row.created_at), 'MMM d, yyyy h:mm a')
                  : '—'}
              </td>
              <td className="px-4 py-3 max-w-xs whitespace-pre-wrap">{row.notes || '—'}</td>
              <td className="px-4 py-3">
                <TransferStatusBadge status={row.status} />
              </td>
              {showActions && (
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canResolve(row) ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => void handleResolve(row.id, true)}
                        disabled={approveLoadingId === row.id || denyLoadingId === row.id}
                        isLoading={approveLoadingId === row.id}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleResolve(row.id, false)}
                        disabled={approveLoadingId === row.id || denyLoadingId === row.id}
                        isLoading={denyLoadingId === row.id}
                      >
                        Deny
                      </Button>
                    </div>
                  ) : ownerAwaitingAdmin(row) ? (
                    <span className="text-xs text-amber-700 font-medium">
                      Awaiting Admin/Super Admin action
                    </span>
                  ) : (
                    <TransferStatusBadge status={row.status} />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading transfer requests…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Pending transfers</h2>
        <p className="text-sm text-gray-500 mb-4">
          Review ownership transfer requests awaiting approval.
        </p>

        {pending.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No pending requests.</p>
        ) : (
          renderTable(pending, { showActions: true })
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Resolved transfers</h2>
        <p className="text-sm text-gray-500 mb-4">Previously approved and denied requests.</p>

        {resolved.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No resolved requests yet.</p>
        ) : (
          renderTable(resolved, { grayed: true })
        )}
      </Card>
    </div>
  );
}
